import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  RivvyLimits,
  RivvyMessage,
  RivvyThread,
} from '@rivethealth/data/rivvy';
import { Observable } from 'rxjs';
import { AuthStore } from './auth';
import { CellStore } from './cell';
import { API_URL } from './common';

@Injectable({ providedIn: 'root' })
export class RivvyApiService {
  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    private readonly authStore: AuthStore,
    private readonly cellStore: CellStore,
    private readonly http: HttpClient,
  ) {}

  createThread(accountId: number): Observable<RivvyThread> {
    return this.http.post<RivvyThread>(
      `${this.apiUrl}/rivvy/${accountId}/threads`,
      {},
    );
  }

  deleteThread(accountId: number, threadId: number): Observable<null> {
    return this.http.delete<null>(
      `${this.apiUrl}/rivvy/${accountId}/threads/${threadId}`,
    );
  }

  getThreads(accountId: number): Observable<RivvyThread[]> {
    return this.http.get<RivvyThread[]>(
      `${this.apiUrl}/rivvy/${accountId}/threads`,
    );
  }

  getThreadMessages(
    accountId: number,
    threadId: number,
  ): Observable<RivvyMessage[]> {
    return this.http.get<RivvyMessage[]>(
      `${this.apiUrl}/rivvy/${accountId}/threads/${threadId}`,
    );
  }

  postQueryStream(
    accountId: number,
    message: RivvyMessage,
    threadId?: number,
  ): Observable<string> {
    return new Observable(observer => {
      const requestUrl = new URL(
        `/rivvy/${accountId}/threads/query/${threadId}`,
        this.cellStore.getCellUrl() || this.apiUrl,
      );

      fetch(requestUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.authStore.getToken()}`,
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(message),
      })
        .then(async response => {
          if (!response || !response.body) observer.complete();
          if (!response.ok) {
            const errorMessage = await response.text();
            observer.error(
              new Error(`HTTP error: ${response.status} - ${errorMessage}`),
            );
            return;
          }
          if (response.body) {
            const reader = response.body
              .pipeThrough(new TextDecoderStream())
              .getReader();
            while (true) {
              const { value, done } = await reader.read();
              if (done) break;

              observer.next(value.replace(/data:/g, ''));
            }
            observer.complete();
          } else {
            observer.complete();
          }
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  modifyThread(
    accountId: number,
    threadId: number,
    title: string,
  ): Observable<RivvyThread> {
    return this.http.put<RivvyThread>(
      `${this.apiUrl}/rivvy/${accountId}/threads/${threadId}`,
      {
        title,
      },
    );
  }
  getLimits(accountId: number): Observable<RivvyLimits> {
    return this.http.get<RivvyLimits>(
      `${this.apiUrl}/rivvy/${accountId}/limits`,
    );
  }
  isEnabled(accountId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/rivvy/${accountId}/enabled`);
  }
}
